import React, { useMemo } from "react";

// 🔹 Utility: Parse Prometheus-like metrics into objects
function parseMetrics(rawText) {
    if (!rawText) return [];

    const lines = rawText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith("#"));

    const metrics = [];

    lines.forEach((line) => {
        // Match: metric_name{optional_labels} value
        const match = line.match(/^([\w:._-]+)(?:\{[^}]*\})?\s+([\d.eE+-]+)$/);
        if (match) {
            const [, name, value] = match;
            metrics.push({
                name,
                value: isNaN(value) ? value : parseFloat(value),
            });
        }
    });

    return metrics;
}

// 🔸 Component: Metrics Panel
export default function MetricsPanel({ data }) {
    const metricsList = useMemo(() => parseMetrics(data?.metrics), [data]);

    return (
        <div className="bg-white rounded-2xl p-5 shadow-md border border-orange-300 hover:shadow-orange-400/40 transition-all duration-300 overflow-hidden">
            <h2 className="text-lg font-semibold mb-3 text-orange-600">Metrics Overview</h2>

            {/* 🟠 When metrics exist */}
            {metricsList?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    {metricsList.slice(0, 15).map((m, i) => (
                        <div
                            key={i}
                            className="p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-all duration-200"
                        >
                            <p className="font-medium text-gray-600 truncate">{m.name}</p>
                            <p className="text-orange-600 font-semibold">{m.value}</p>
                        </div>
                    ))}
                </div>
            ) : (
                // 🟠 No metrics fallback
                <div className="text-center text-gray-500 bg-orange-50 rounded-lg p-5">
                    No metrics available
                </div>
            )}

            {/* 🟠 Raw Data (collapsible) */}
            {data?.metrics && (
                <details className="mt-4">
                    <summary className="cursor-pointer text-orange-500 font-semibold select-none">
                        View Raw Data
                    </summary>
                    <pre className="text-xs text-gray-700 bg-orange-50 rounded-lg p-3 mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed border border-orange-100">
                        {data.metrics}
                    </pre>
                </details>
            )}
        </div>
    );
}
